import { parseEncodedObject } from './ss-utils.js';
import { BaseCard } from './ss-BaseCard.js';

class ProductCards extends BaseCard {
  constructor(containerId) {
    super(containerId);
  }

  noResults() {
    this.container.innerHTML = '<p>No products match your search...</p>';
  }

  renderSkeletons(count) {
    for (let i = 0; i < count; i++) {
      const skeleton = document.createElement('li');
      skeleton.classList.add('js__grid-item', 'product-card', 'skeleton');
      skeleton.innerHTML = `
        <div class="product-card-image">
          <div class="product-card-image-skeleton"></div>
        </div>
        <div class="product-card-content">
          <div class="product-card-title-skeleton"></div>
          <div class="product-card-price-skeleton"></div>
        </div>
      `;
      this.container.appendChild(skeleton);
    }
  }

  createVariantElements(variants, productId) {
    if (!variants?.length) return '';

    return variants.map((variant, index) => {
      const disabled = variant.inventory_quantity <= 0 ? 'disabled' : '';
      return `
        <input type="radio" id="${productId}-1-${index}" name="Size" value="${variant.title}" form="${productId}-single__cart-item" ${disabled}>
        <label for="${productId}-1-${index}">
          ${variants.length === 1 ? 'One Size' : variant.title}
        </label>
      `;
    }).join('');
  }

  createVariantSection(variants, product) {
    if (!variants?.length) return '';

    const variantElements = this.createVariantElements(variants, product.uid);
    const variantsWithOptions = variants.map(variant => ({
      ...variant,
      options: [variant.title],
      id: variant.id.split('/').pop(),
    }));

    return `
      <div class="cart__badge" id="product_variant_id">
        <div class="cart__options">
          <options-radio class="cart__variants no-js-hidden" data-section="${product.uid}" data-url="/products/${product.handle}">
            <fieldset class="js product-form__input">
              ${variantElements}
              <span for="" class="more__items" style="display: none;" data-link="/products/${product.handle}">+</span>
            </fieldset>
            <script type="application/json" class="product-variants-dump updated">
              ${JSON.stringify(variantsWithOptions)}
            </script>
          </options-radio>
        </div>
      </div>
    `;
  }

  render(products, clearContainer = false) {
    if (clearContainer) {
      this.clearContainer();
    }

    products.forEach(product => {
      if (!product) return;

      if (product.isMerchandising) {
        let cleanContent = product.content;
        const scriptRegex = /^<script[^>]*>([\s\S]*)<\/script>$/i;
        const match = scriptRegex.exec(cleanContent);
        if (match && match[1]) {
          cleanContent = match[1].trim();
        }
        const li = document.createElement('li');
        li.classList.add('js__grid-item', 'product-card', 'merchandising-card');
        li.innerHTML = cleanContent;
        this.container.appendChild(li);
        return;
      }

      const variants = this.processVariants(parseEncodedObject(product.variants));
      const { productName, productColor } = this.getProductNameAndColor(product.name);
      const images = this.formatImages(product.images, product.images_alt?.split('|'), product.name);
      const variantSection = this.createVariantSection(variants, product);
      const priceSection = this.createPriceSection(product.msrp, product.price, product.uid);
      const cardBadge = this.createCardBadge(product, variants);
      const swatches = this.createSwatchesSection(product);

      const productCard = document.createElement('li');
      productCard.classList.add('js__grid-item', 'product-card');
      productCard.dataset.product = JSON.stringify(product);
      productCard.href = `/products/${product.handle}`;

      productCard.innerHTML = `
        <div class="card-wrapper">
          <div class="card-information">
            <div class="card-information__wrapper">
              <h3 class="card-information__text h5">
                <a href="/products/${product.handle}" class="full-unstyled-link" onmousedown="IntelliSuggest.clickItem(this, { siteId: '40t3km', intellisuggestData: '${product.intellisuggestData}', intellisuggestSignature: '${product.intellisuggestSignature}' })">${productName}${productColor}</a>
              </h3>
              <span class="caption-large light"></span>

              <div class="price">
                <div>
                  ${priceSection}
                  <small class="unit-price caption hidden">
                    <span class="visually-hidden">Unit price</span>
                    <span class="price-item price-item--last">
                      <span></span>
                      <span>/</span>
                      <span class="visually-hidden">&nbsp;per&nbsp;</span>
                      <span></span>
                    </span>
                  </small>
                </div>
              </div>
              ${swatches}
              <product-form class="product-form cart__form-button">
                <div class="product-form__error-message-wrapper" role="alert" hidden="">
                  <svg aria-hidden="true" focusable="false" role="presentation" class="icon icon-error" viewBox="0 0 13 13">
                    <circle cx="6.5" cy="6.50049" r="5.5" stroke="white" stroke-width="2"></circle>
                    <circle cx="6.5" cy="6.5" r="5.5" fill="#EB001B" stroke="#EB001B" stroke-width="0.7"></circle>
                    <path d="M5.87413 3.52832L5.97439 7.57216H7.02713L7.12739 3.52832H5.87413ZM6.50076 9.66091C6.88091 9.66091 7.18169 9.37267 7.18169 9.00504C7.18169 8.63742 6.88091 8.34917 6.50076 8.34917C6.12061 8.34917 5.81982 8.63742 5.81982 9.00504C5.81982 9.37267 6.12061 9.66091 6.50076 9.66091Z" fill="white"></path>
                    <path d="M5.87413 3.17832H5.51535L5.52424 3.537L5.6245 7.58083L5.63296 7.92216H5.97439H7.02713H7.36856L7.37702 7.58083L7.47728 3.537L7.48617 3.17832H7.12739H5.87413ZM6.50076 10.0109C7.06121 10.0109 7.5317 9.57872 7.5317 9.00504C7.5317 8.43137 7.06121 7.99918 6.50076 7.99918C5.94031 7.99918 5.46982 8.43137 5.46982 9.00504C5.46982 9.57872 5.94031 10.0109 6.50076 10.0109Z" fill="white" stroke="#EB001B" stroke-width="0.7"></path>
                  </svg>
                  <span class="product-form__error-message"></span>
                </div>
                <form method="post" action="/cart/add" id="product-form-${product.uid}" accept-charset="UTF-8" class="form product-form product-cart__form" enctype="multipart/form-data" data-productid="${product.uid}" novalidate="novalidate" data-type="add-to-cart-form">
                  <input type="hidden" name="form_type" value="product">
                  <input type="hidden" name="utf8" value="✓">
                  <input type="hidden" name="id" data-productid="${product.uid}" value="${product.ss_id}" data-variantname="null">
                  <div class="product-form__buttons">
                    <button type="submit" name="add" class="product-form__submit button button--full-width button--primary" ${(variants && variants.length > 1) || product.ss_available === "0" ? 'disabled' : ''}>
                      <span>${(variants && variants.length > 1) ? 'Select a Size' : product.ss_available === "0" ? 'Sold Out' : 'Add to Cart'}</span>
                      <div class="loading-overlay__spinner hidden">
                        <svg aria-hidden="true" focusable="false" role="presentation" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
                          <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
                        </svg>
                      </div>
                    </button>
                  </div>
                  <input type="hidden" name="product-id" value="${product.uid}">
                </form>
              </product-form>
            </div>
          </div>

          <div class="card card--product card--outline" tabindex="-1">
            <div class="card__inner">
              <div>
                <div class="media media-grey-background media--adapt media--hover-effect" style="padding-bottom: 100.0%;">
                  <img src="${images[0].url}" alt="${images[0].alt}" loading="lazy" class="motion-reduce main-product-image" width="600" height="600">
                  ${images[1] && images[1].url !== images[0].url ? `<img src="${images[1].url}" alt="${images[1].alt}" loading="lazy" class="motion-reduce secondary-product-image" width="600" height="600">` : ''}
                </div>
              </div>
              <div class="card__badge">
                ${cardBadge}
              </div>
              ${variantSection}
            </div>
          </div>
        </div>
      `;

      this.container.appendChild(productCard);
    });
  }
}

window.ProductCards = ProductCards;